import {
  deriveTiaReadiness,
  readServerSessionConfig,
  readTiaProjectRef,
  resolveTiaTarget,
  TIA_READINESS_TEXT,
  TIA_VALIDATION_CODES,
  type ResolvedTiaTarget,
  type ServerSessionConfig,
  type TiaAgentFacts,
  type TiaProjectRef,
  type TiaReadiness,
  type TiaReadinessResult,
  type TiaText,
  type TiaValidationTarget,
  type TiaWorkMode,
} from '@realplc/types';
import { apiFetch } from './auth';
import {
  TIA_CONNECTOR_CAUSE_TEXT,
  TIA_NO_PROJECT_TEXT,
  type TiaConnectorCause,
  type TiaPickerIntent,
} from '../utils/tiaSituations';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Asks the TIA project picker above the input box to open (TIA 根治 P2: T4's
 * gate button sends it, T5's picker listens). `intent` is the way out the
 * reader chose: choose a project, or a new one, which only sets the reference
 * to new (E11 ④).
 */
export const OPEN_TIA_PROJECT_PICKER_EVENT = 'realplc:open-tia-picker';

export interface OpenTiaProjectPickerDetail {
  intent: TiaPickerIntent;
}

export function requestOpenTiaProjectPicker(intent: TiaPickerIntent): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<OpenTiaProjectPickerDetail>(OPEN_TIA_PROJECT_PICKER_EVENT, { detail: { intent } }));
  }
}

export interface PairingCodeResponse {
  code: string;
  expiresAt: number;
  wsUrl?: string;
}

interface PairingCodeApiResponse {
  code?: string;
  pairingCode?: string;
  expiresAt?: number;
  expiresAtUtc?: string;
  expiresInSeconds?: number;
  wsUrl?: string;
}

export interface OpennessAgentRecord {
  id: string;
  organizationId: string;
  projectId?: string | null;
  agentName: string;
  machineId?: string | null;
  agentInstanceId?: string | null;
  agentVersion?: string | null;
  /** Current backend field persisted from Agent registration/heartbeats. */
  capabilitySnapshot?: Record<string, unknown> | null;
  /** Legacy compatibility field used by older gateway responses. */
  capabilities?: Record<string, unknown> | null;
  status: 'online' | 'offline' | 'pairing' | 'busy' | 'degraded' | 'revoked';
  lastSeenAt?: number | null;
}

export interface AgentStatusResponse {
  status: 'online' | 'offline' | 'pairing';
  connectionState?: 'online' | 'offline';
  contextualProjectId?: string;
  stale?: boolean;
  agentName?: string;
  lastSeenAt?: number;
  agents: OpennessAgentRecord[];
  /**
   * Every Agent's TIA facts, offline ones included (TIA 根治 P2, T3), which
   * the gateway builds in one place from the Agents' reports and pairings.
   *
   * The response also carries `readinessState`, the gateway's older union,
   * which the browser no longer types or reads: it folds states together (a
   * new Agent still checking and an old one with nothing open both arrive as
   * project_not_open). Readiness is readTiaReadiness's, from these facts.
   */
  tiaAgents?: TiaAgentFacts[];
}

/**
 * The TIA readiness the composer's TIA button, the Agent settings dialog and
 * the post-generation card all show (TIA 根治 P2, T4). One computation, so the
 * three cannot word one state three ways, from the same two contract functions
 * the gateway's status and eligibility check call.
 */
export interface TiaReadinessView extends TiaReadinessResult {
  /** The Agent the state is about; absent when no Agent is usable. */
  agentId?: string;
  target: ResolvedTiaTarget;
  /** The TIA button's text. */
  label: TiaText;
  /** One sentence on what is going on and what to do. */
  explanation: TiaText;
  /**
   * A validation submitted now runs: the Agent is ready, or busy and the job
   * queues behind the running one, which the gateway allows since T3.
   */
  canValidate: boolean;
  /**
   * The conversation is in no RealPLC project, and nothing of the Agent's own
   * is in the way (AGENT_OWN_STATES): the words say the conversation has no
   * project, whatever `state`, the Agent's, holds. `canValidate` is false
   * without a project either way.
   */
  noProject: boolean;
}

/**
 * The states deriveTiaReadiness reports before `checking`: faults of the Agent
 * itself. They follow it into every conversation, and no project fixes them,
 * so a conversation in no project keeps their words (T4 re-review).
 */
const AGENT_OWN_STATES: ReadonlySet<TiaReadiness> = new Set<TiaReadiness>([
  'offline',
  'auth_required',
  'not_authorized',
  'environment_not_ready',
  'capability_missing',
]);

/**
 * The readiness for validating in `projectId`, from `/agents/status`.
 *
 * `tiaAgents` goes to resolveTiaTarget and deriveTiaReadiness, and the words
 * are the readiness table's (a busy Agent's say its validation queues), with
 * two exceptions written in tiaSituations.ts: a connector the Agent itself
 * switched off or quarantined says so instead of asking for a newer Agent,
 * and a conversation in no RealPLC project says it has none from `checking`
 * on, since nothing can be validated there; the Agent's own faults before
 * that keep their words.
 *
 * `sessionRef` is the open conversation's project reference
 * (useSessionTiaTargetStore), so that every reader resolves the target the
 * next turn validates in. Without one the target is E7's default: the one
 * open project, a new one, or a choice.
 */
export function readTiaReadiness(
  status: Pick<AgentStatusResponse, 'status' | 'connectionState' | 'agents' | 'tiaAgents'> | null | undefined,
  projectId: string | null | undefined,
  sessionRef?: TiaProjectRef,
): TiaReadinessView {
  const project = projectId ?? undefined;
  const { target, agentId, readiness } = readinessFromFacts(status, project, sessionRef);
  // A connected workstation may be ready outside a project, but validation
  // still needs a RealPLC project for its artifacts and access checks.
  const noProject = !project && !AGENT_OWN_STATES.has(readiness.state);
  const cause = readiness.state === 'capability_missing' ? tiaConnectorCause(status?.agents, agentId) : undefined;
  const words = noProject
    ? TIA_NO_PROJECT_TEXT
    : cause ? TIA_CONNECTOR_CAUSE_TEXT[cause] : TIA_READINESS_TEXT[readiness.state];
  return {
    ...readiness,
    ...(agentId ? { agentId } : {}),
    target,
    label: words.label,
    explanation: words.explanation,
    canValidate: Boolean(project) && (readiness.state === 'ready' || readiness.state === 'busy'),
    noProject,
  };
}

/**
 * The target and its readiness from the Agents' facts.
 *
 * A status without `tiaAgents` comes from a gateway before T3, which is not
 * an organization with no Agent (`[]`): the connection says whether an Agent
 * answers, and nothing yet says what it can do, so it is checking rather than
 * offline (T4 review). `readinessState` is no fallback: it folds states
 * together.
 */
function readinessFromFacts(
  status: Pick<AgentStatusResponse, 'status' | 'connectionState' | 'tiaAgents'> | null | undefined,
  project: string | undefined,
  sessionRef: TiaProjectRef | undefined,
): { target: ResolvedTiaTarget; agentId?: string; readiness: TiaReadinessResult } {
  const agents = status?.tiaAgents;
  if (!Array.isArray(agents)) {
    const online = (status?.connectionState ?? status?.status) === 'online';
    return { target: { kind: 'none' }, readiness: { state: online ? 'checking' : 'offline' } };
  }
  // No `projectDefaultRef`: the web has no reader for a project's TIA settings (P2 本阶段不做).
  const target = resolveTiaTarget({ agents, projectId: project, sessionRef });
  const agentId = target.kind === 'none' ? undefined : target.agentId;
  const facts = agentId ? agents.find((agent) => agent.agentId === agentId) ?? null : null;
  return { target, ...(agentId ? { agentId } : {}), readiness: deriveTiaReadiness(facts, target, project) };
}

/**
 * Why the Agent's TIA connector declares no actions, when the Agent's own
 * report says: switched off, or quarantined. The facts cannot tell either from
 * an Agent too old to validate, since the gateway drops a disabled or
 * quarantined connector's actions (connectorDeclaredActions); the connector
 * block of the report the status route returns with each Agent can.
 */
function tiaConnectorCause(
  agents: OpennessAgentRecord[] | undefined,
  agentId: string | undefined,
): TiaConnectorCause | undefined {
  const agent = agentId ? agents?.find((item) => item.id === agentId) : undefined;
  const connectors = (agent?.capabilitySnapshot ?? agent?.capabilities)?.connectors;
  if (!Array.isArray(connectors)) return undefined;
  const tia = connectors.find((item) => (
    Boolean(item) && typeof item === 'object' && (item as Record<string, unknown>).connectorId === 'tia'
  )) as Record<string, unknown> | undefined;
  if (!tia) return undefined;
  if (tia.state === 'quarantined') return 'quarantined';
  return tia.enabled === false || tia.state === 'disabled' ? 'disabled' : undefined;
}

export interface OpennessJobResponse {
  jobId: string;
  status: string;
  result?: Record<string, unknown>;
  fullResult?: Record<string, unknown>;
  error?: string;
}

export interface TiaValidationRunResponse {
  runId: string;
  attemptId?: string;
  jobId: string;
  /** `running` or `queued`; `blocked` when the run was made but nothing could be compiled, with `code`. */
  status: string;
  dispatched?: boolean;
  /** Why a `blocked` run stopped, a code tiaSituations words. */
  code?: string;
  message?: string;
  run?: TiaValidationRunSummary;
  attempts?: TiaValidationAttemptSummary[];
  jobs?: TiaValidationJobSummary[];
  results?: TiaValidationResultSummary[];
}

/**
 * A TIA request the gateway refused, with its code: a blocker tiaSituations
 * words (TIA_PROJECT_NOT_SELECTED, TIA_PROJECT_UNSAVED…), or one of
 * AGENT_OFFLINE, AGENT_UPGRADE_REQUIRED, AGENT_TARGET_NOT_AUTHORIZED,
 * TIA_SAFETY_REFUSED, SESSION_NOT_FOUND. The page says the code's words, not
 * the gateway's message, which is for logs.
 */
export class TiaRequestError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'TiaRequestError';
    this.status = status;
    this.code = code;
  }
}

async function tiaRequestError(response: Response, fallback: string): Promise<TiaRequestError> {
  const data = await response.json().catch(() => null) as { message?: unknown; error?: unknown } | null;
  const code = typeof data?.error === 'string' && data.error.trim() ? data.error.trim() : undefined;
  const message = typeof data?.message === 'string' && data.message.trim() ? data.message : code ?? fallback;
  return new TiaRequestError(message, response.status, code);
}

/** What a picker action (T8) answers: the job, and with `wait`, how it ended on the Agent. */
export interface TiaPickerActionResult {
  jobId: string;
  agentId: string;
  /** `sent` or `queued` until the job ends; then the job's end, e.g. `completed`, `failed`. */
  status: string;
  dispatched: boolean;
  result?: Record<string, unknown> | null;
  /** The Agent's code when the job failed, e.g. TIA_VALIDATION_COPY_CLEANED. */
  failureCode?: string | null;
  error?: string | null;
}

/** How a job ended in failure, as the gateway's queue records it. */
const FAILED_JOB_STATUSES: ReadonlySet<string> = new Set(['failed', 'infra_failed', 'cancelled', 'expired', 'timeout', 'blocked']);

/**
 * A picker action's end, as the page says it: done on the Agent, sent and
 * still under way (an action that outlasts the wait, or that was not waited
 * for), or failed, with the Agent's code when it gave one. A project already
 * open in a TIA window is done, with its code for the words: it is open,
 * which is what opening it was for.
 */
export function readTiaPickerOutcome(result: TiaPickerActionResult): { outcome: 'done' | 'sent' | 'failed'; code?: string } {
  const code = result.failureCode?.trim() || undefined;
  if (code?.toUpperCase() === TIA_VALIDATION_CODES.TIA_PROJECT_ALREADY_OPEN) return { outcome: 'done', code };
  if (code || FAILED_JOB_STATUSES.has(result.status)) return { outcome: 'failed', ...(code ? { code } : {}) };
  return { outcome: result.status === 'completed' ? 'done' : 'sent' };
}

/** How long the page waits for an action that opens a window on the Agent: the gateway's limit. */
const PICKER_WAIT_MS = 60_000;

async function pickerAction(
  path: string,
  body: Record<string, unknown>,
  options: { wait: boolean },
): Promise<TiaPickerActionResult> {
  const query = options.wait ? `?wait=1&timeoutMs=${PICKER_WAIT_MS}` : '';
  const response = await apiFetch(`${API_BASE}${path}${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await tiaRequestError(response, 'The RealPLC Agent could not take the request');
  return response.json() as Promise<TiaPickerActionResult>;
}

function tiaPath(projectId: string, rest: string): string {
  return `/api/projects/${encodeURIComponent(projectId)}/tia/${rest}`;
}

/** The picker's refresh (T8): the Agent lists its projects again; the new list arrives with its heartbeat. */
export function refreshTiaProjects(projectId: string, agentId?: string): Promise<TiaPickerActionResult> {
  return pickerAction(tiaPath(projectId, 'projects/refresh'), agentId ? { agentId } : {}, { wait: false });
}

/**
 * Opens a project, or a verdict's `openable` result, in the Agent's own TIA
 * window, never the user's (E12), waiting for the Agent to say how it went.
 */
export function openTiaProject(projectId: string, agentId: string, id: string): Promise<TiaPickerActionResult> {
  return pickerAction(tiaPath(projectId, 'projects/open'), { agentId, id }, { wait: true });
}

/** Starts a TIA Portal with a window on the Agent's machine, waiting for the Agent to say how it went. */
export function startTiaPortal(projectId: string, agentId?: string): Promise<TiaPickerActionResult> {
  return pickerAction(tiaPath(projectId, 'portal/start'), agentId ? { agentId } : {}, { wait: true });
}

/** What the gateway stored for a conversation's project choice (T8), and the snapshot's revision. */
export interface TiaProjectChoiceStored {
  tiaProjectRef: TiaProjectRef | null;
  /** The server's whole account of its snapshot fields after the write. */
  sessionTiaTarget: ServerSessionConfig;
  sessionConfigRevision?: number;
  resumed: unknown[];
}

function readRevision(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Stores the user's project choice for a conversation now, null for a clear,
 * and resumes that conversation's validations parked on the choice (T8,
 * TIA_PROJECT_NOT_SELECTED or TIA_PROJECT_REF_NOT_FOUND).
 */
export async function storeTiaProjectChoice(
  projectId: string,
  sessionId: string,
  tiaProjectRef: TiaProjectRef | null,
): Promise<TiaProjectChoiceStored> {
  const response = await apiFetch(`${API_BASE}${tiaPath(projectId, `sessions/${encodeURIComponent(sessionId)}/project-choice`)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tiaProjectRef }),
  });
  if (!response.ok) throw await tiaRequestError(response, 'Failed to store the project choice');
  const data = await response.json() as Record<string, unknown>;
  return {
    tiaProjectRef: readTiaProjectRef(data.tiaProjectRef) ?? null,
    sessionTiaTarget: readServerSessionConfig(data.sessionTiaTarget),
    ...(readRevision(data.sessionConfigRevision) !== undefined ? { sessionConfigRevision: readRevision(data.sessionConfigRevision) } : {}),
    resumed: Array.isArray(data.resumed) ? data.resumed : [],
  };
}

/**
 * The conversation's project reference as the server stores it, with the
 * snapshot's revision (T8's picker list, `?sessionId=`): what the page reads
 * again when a validation wrote a created project's reference outside a turn.
 */
export async function loadSessionTiaProjectRef(
  projectId: string,
  sessionId: string,
): Promise<{ tiaProjectRef?: TiaProjectRef; sessionConfigRevision?: number }> {
  const response = await apiFetch(`${API_BASE}${tiaPath(projectId, `projects?sessionId=${encodeURIComponent(sessionId)}`)}`);
  if (!response.ok) throw await tiaRequestError(response, 'Failed to load the conversation\'s TIA project');
  const data = await response.json() as Record<string, unknown>;
  const tiaProjectRef = readTiaProjectRef(data.tiaProjectRef);
  const sessionConfigRevision = readRevision(data.sessionConfigRevision);
  return {
    ...(tiaProjectRef ? { tiaProjectRef } : {}),
    ...(sessionConfigRevision !== undefined ? { sessionConfigRevision } : {}),
  };
}

export interface TiaValidationRunSummary {
  id: string;
  projectId: string;
  codeArtifactId: string;
  status: string;
  currentAttempt?: number;
  finalAttemptId?: string | null;
  finalArtifactId?: string | null;
  failureReason?: string | null;
  finalSummary?: Record<string, unknown> | null;
  runDeadlineAt?: number | null;
  startedAt?: number | null;
  completedAt?: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface TiaValidationAttemptSummary {
  id: string;
  runId: string;
  attemptNo: number;
  latestJobId?: string | null;
  successfulJobId?: string | null;
  codeArtifactId: string;
  sourceHash?: string | null;
  bundleHash?: string | null;
  failureClass?: string | null;
  diagnosticsSummary?: Record<string, unknown> | null;
  status: string;
  startedAt?: number | null;
  completedAt?: number | null;
}

export interface TiaValidationJobSummary {
  id: string;
  agentId?: string | null;
  attemptId?: string | null;
  dispatchNo?: number;
  infraRetryNo?: number;
  jobType: string;
  status: string;
  leaseEpoch?: number;
  leaseExpiresAt?: number | null;
  failureCode?: string | null;
  errorMessage?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface TiaValidationResultSummary {
  resultId: string;
  jobId: string;
  attemptId?: string | null;
  leaseEpoch?: number;
  sourceHash?: string | null;
  bundleHash?: string | null;
  accepted: boolean;
  rejectionReason?: string | null;
  ackStatus: string;
  normalizedDiagnostics?: Record<string, unknown> | null;
  receivedAt: number;
  committedAt?: number | null;
}

export interface TiaValidationRunDetail {
  run: TiaValidationRunSummary;
  attempts: TiaValidationAttemptSummary[];
  jobs: TiaValidationJobSummary[];
  results: TiaValidationResultSummary[];
  progress?: { jobId?: string; stage: string; percent: number | null; message: string; at?: number } | null;
  /** What the latest compile ran against, for the verdict (the gateway's `run.tia.verdict`, T8). */
  target?: TiaValidationTarget;
}

const validationRunRequestIds = new Map<string, { id: string; expiresAt: number }>();

export async function createRealPlcAgentPairingCode(projectId?: string | null): Promise<PairingCodeResponse> {
  const response = await apiFetch(`${API_BASE}/api/agent-gateway/pairing-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(projectId ? { projectId } : {}),
  });
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to create pairing code'));
  }
  const data = await response.json() as PairingCodeApiResponse;
  return normalizePairingCodeResponse(data);
}

/** @deprecated Use createRealPlcAgentPairingCode; pairing belongs to Agent Core. */
export const createTiaPairingCode = createRealPlcAgentPairingCode;

export async function loadTiaAgentStatus(projectId?: string | null): Promise<AgentStatusResponse> {
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const response = await apiFetch(`${API_BASE}/api/agent-gateway/agents/status${query}`);
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to load RealPLC Agent status'));
  }
  return response.json() as Promise<AgentStatusResponse>;
}

export async function runTiaAgentSnapshot(projectId?: string | null): Promise<OpennessJobResponse> {
  const response = await apiFetch(`${API_BASE}/api/agent-gateway/jobs/snapshot?wait=1&timeoutMs=120000`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(projectId ? { projectId } : {}),
  });
  if (!response.ok) {
    throw new Error(await readError(response, 'TIA Portal connector environment check failed'));
  }
  return response.json() as Promise<OpennessJobResponse>;
}

/**
 * 「立即验证」 and the re-validations after a blocked run (T8's one run path).
 * It carries the page's TIA target, so the run goes where the page showed it:
 * the conversation's reference, the conversation, whose stored snapshot fills
 * in the rest and takes a created project's reference, and the work mode
 * (E12); and `saveAsCopyConsent` for the one run the user let switch their
 * TIA window to a save-as copy (E5 ①). A refusal throws a TiaRequestError
 * whose code says why, 409 with a blocker when no run was made.
 */
export async function createTiaValidationRun(input: {
  projectId: string;
  codeArtifactId: string;
  agentId?: string;
  approvalId?: string;
  clientRequestId?: string;
  tiaProjectRef?: TiaProjectRef;
  sessionId?: string | null;
  tiaWorkMode?: TiaWorkMode;
  saveAsCopyConsent?: boolean;
}): Promise<TiaValidationRunResponse> {
  const clientRequestId = input.clientRequestId ?? getValidationRunRequestId(input);
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(input.projectId)}/tia/validation-runs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': clientRequestId,
    },
    body: JSON.stringify({
      codeArtifactId: input.codeArtifactId,
      agentId: input.agentId,
      approvalId: input.approvalId,
      clientRequestId,
      ...(input.tiaProjectRef ? { tiaProjectRef: input.tiaProjectRef } : {}),
      ...(input.sessionId ? { sessionId: input.sessionId } : {}),
      ...(input.tiaWorkMode ? { tiaWorkMode: input.tiaWorkMode } : {}),
      ...(input.saveAsCopyConsent ? { saveAsCopyConsent: true } : {}),
    }),
  });
  if (!response.ok) {
    throw await tiaRequestError(response, 'Failed to create TIA validation run');
  }
  return response.json() as Promise<TiaValidationRunResponse>;
}

export async function listTiaValidationRuns(projectId: string): Promise<{ runs: TiaValidationRunSummary[] }> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(projectId)}/tia/validation-runs`);
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to load TIA validation runs'));
  }
  return response.json() as Promise<{ runs: TiaValidationRunSummary[] }>;
}

export async function loadTiaValidationRun(projectId: string, runId: string): Promise<TiaValidationRunDetail> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(projectId)}/tia/validation-runs/${encodeURIComponent(runId)}`);
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to load TIA validation run'));
  }
  return response.json() as Promise<TiaValidationRunDetail>;
}

export async function deliverTiaWorkflowUnvalidated(
  projectId: string,
  workflowRunId: string,
): Promise<{ workflowRunId: string; artifactId: string; stageStatus: string; validationStatus: 'unvalidated' }> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/tia/workflows/${encodeURIComponent(workflowRunId)}/deliver-unvalidated`,
    { method: 'POST' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to deliver the artifact without TIA validation'));
  }
  return response.json();
}

export async function cancelTiaValidationRun(projectId: string, runId: string): Promise<{ runId: string; status: string }> {
  const response = await apiFetch(`${API_BASE}/api/projects/${encodeURIComponent(projectId)}/tia/validation-runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to cancel TIA validation run'));
  }
  return response.json() as Promise<{ runId: string; status: string }>;
}

/**
 * Open the agent-status stream.
 *
 * EventSource cannot set an Authorization header, so the session is first
 * exchanged for a single-use ticket over an authenticated POST. Only that
 * ticket appears in the stream URL — which lands in nginx access logs, browser
 * history and Referer headers, where an access token must never go.
 */
export async function createAgentStatusEventSource(
  projectId?: string | null,
): Promise<EventSource | null> {
  if (typeof EventSource === 'undefined') return null;

  const response = await apiFetch(
    `${API_BASE}/api/agent-gateway/agents/status/stream-ticket`,
    { method: 'POST' },
  );
  if (!response.ok) return null;

  const { ticket } = await response.json() as { ticket?: string };
  if (!ticket) return null;

  const params = new URLSearchParams({ ticket });
  if (projectId) params.set('projectId', projectId);
  return new EventSource(`${API_BASE}/api/agent-gateway/agents/status/stream?${params.toString()}`);
}

function getValidationRunRequestId(input: {
  projectId: string;
  codeArtifactId: string;
  approvalId?: string;
  saveAsCopyConsent?: boolean;
}): string {
  // A consented re-validation is another request than the run it follows.
  const key = `${input.projectId}:${input.codeArtifactId}:${input.approvalId ?? ''}:${input.saveAsCopyConsent ? 'save-as' : ''}`;
  const now = Date.now();
  const existing = validationRunRequestIds.get(key);
  if (existing && existing.expiresAt > now) return existing.id;
  const id = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `tia-${now}-${Math.random().toString(36).slice(2)}`;
  validationRunRequestIds.set(key, { id, expiresAt: now + 60_000 });
  return id;
}

async function readError(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => null) as { message?: string; error?: string } | null;
  return data?.message || data?.error || fallback;
}

function normalizePairingCodeResponse(data: PairingCodeApiResponse): PairingCodeResponse {
  const code = data.code ?? data.pairingCode;
  if (!code) {
    throw new Error('Pairing code response was missing a code.');
  }

  let expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : undefined;
  if (!expiresAt && data.expiresAtUtc) {
    const parsed = Date.parse(data.expiresAtUtc);
    if (Number.isFinite(parsed)) expiresAt = parsed;
  }
  if (!expiresAt && typeof data.expiresInSeconds === 'number') {
    expiresAt = Date.now() + data.expiresInSeconds * 1000;
  }

  return {
    code,
    expiresAt: expiresAt ?? 0,
    wsUrl: data.wsUrl,
  };
}
